import { useFileUpload } from "@/dentlab/src/hooks/useFileUpload";
import { useRef, useState } from "react";
import { OnboardingStep } from "../onboarding-components/onboarding-step";
import { Logger } from "@/lib/logger/Logger";
import { ONBOARDING_STEPS } from "@/dentlab/src/constants/onboarding";
import { MIGRATION_DECISION } from "@/dentlab/src/constants/onboarding";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { uploadFileWithProvidedPath } from "@/dentlab/src/hooks/useStorage";
import { StorageBucketsEnum } from "@/dentlab/src/types/enums";
import { supabase } from "@/lib/supabase";
import { useAttributesStore } from "@/dentlab/src/store/Attributes";
import { createFileMetadata } from "@/lib/utils/file-utils";
import { sanitizeFileName } from "@/dentlab/src/lib/utils/utils-functions";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { Upload } from "lucide-react";
import { Input } from "@/dentlab/src/components/ui/input";
import { X } from "lucide-react";
import { Checkbox } from "@/dentlab/src/components/ui/checkbox";
import { StepProps } from "./types";

interface StepMigrationProps extends StepProps {}

export const StepMigration: React.FC<StepMigrationProps> = ({
	onComplete,
	stepNumber,
	totalSteps,
	canGoBack,
	onGoBack,
}) => {
	const navigate = useNavigate();
	const { setOnboardingAttribute } = useAttributesStore((state) => ({
		setOnboardingAttribute: state.setOnboardingAttribute,
	}));

	const fileInputRef = useRef<HTMLInputElement>(null);
	const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [migrationDecision, setMigrationDecision] = useState(false);

	const { isDragging, isUploading, dragHandlers } = useFileUpload({
		onFileUpload: (files: File[]) => {
			setUploadedFiles((prev) => [...prev, ...files]);
		},
	});

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			const filesArray = Array.from(e.target.files);
			setUploadedFiles([...uploadedFiles, ...filesArray]);
		}
	};

	const removeFile = (index: number) => {
		setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
	};

	const handleCompleteOnboarding = async () => {
		setLoading(true);

		try {
			// Save migration decision
			await setOnboardingAttribute(
				ONBOARDING_STEPS.MIGRATION_DECISION,
				migrationDecision
					? MIGRATION_DECISION.MIGRATE_FROM_DLXP
					: MIGRATION_DECISION.NO_MIGRATION,
				"org"
			);

			// Refresh the entire store to get updated profile with onboarding status
			const organization = useCentralStore.getState().organization;

			if (!organization?.id) {
				setErrorMessage("Organisation nicht gefunden");
				setLoading(false);
				return;
			}

			// Upload files if any
			if (uploadedFiles.length > 0) {
				for (const file of uploadedFiles) {
					const filePath = `${
						organization.id
					}/onboarding/${sanitizeFileName(file.name)}`;
					const { success } = await uploadFileWithProvidedPath({
						fileBody: file,
						filePath: filePath,
						bucketName: StorageBucketsEnum.ORG_V1,
					});

					if (!success) {
						Logger.error("Error uploading file: " + file.name);
					} else {
						await supabase.from("files").insert({
							organization_id: organization.id,
							path_name: filePath,
							file_name: file.name,
							bucket_name: StorageBucketsEnum.ORG_V1,
							meta_data: createFileMetadata(file),
						});
					}
				}
			}
		} catch (error) {
			Logger.error("Error in onboarding: " + String(error));
			setErrorMessage("Ein Fehler ist aufgetreten");
			return;
		} finally {
			setLoading(false);
			if (migrationDecision) {
				navigate("/migration");
			} else {
				onComplete();
			}
		}
	};

	return (
		<OnboardingStep
			stepTitle="Konfiguration abschliessen"
			stepDescription="Schliessen Sie Ihre Einrichtung ab. Sie können optional Dokumente für die PDF-Konfiguration hochladen und bei Bedarf eine Datenmigration von Dental lab XP durchführen."
			canGoBack={canGoBack}
			onGoBackClick={onGoBack}
			buttonText={
				migrationDecision ? "Zur Migration" : "Einführung abschliessen"
			}
			buttonDisabled={false}
			loading={loading}
			errorMessage={errorMessage}
			onErrorMessageClose={() => setErrorMessage(null)}
			onButtonClick={handleCompleteOnboarding}
			stepNumber={stepNumber}
			totalSteps={totalSteps}
		>
			<div className="space-y-4 sm:space-y-8">
				<div className="space-y-2 sm:space-y-3">
					<div>
						<h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">
							Dokumente hochladen (optional)
						</h3>
						<p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4">
							Laden Sie Lieferscheine, Kostenvoranschläge,
							Rechnungen oder andere PDFs hoch, die sie aktuell
							verwenden. Das dentDESK-Team kann diese nutzen, um
							Ihre PDF-Vorlagen entsprechend zu konfigurieren. Sie
							können die Konfiguration auch selbst vornehmen.
						</p>
					</div>
					<div>
						<div
							{...dragHandlers}
							onClick={() => fileInputRef.current?.click()}
							className={clsx(
								"border-2 border-dashed rounded-lg p-4 sm:p-6 text-center transition-colors cursor-pointer",
								isDragging
									? "border-blue-500 bg-blue-50"
									: "border-gray-300 hover:border-gray-400"
							)}
						>
							{isDragging ? (
								<>
									<Upload className="h-6 sm:h-8 w-6 sm:w-8 mx-auto mb-1 sm:mb-2 text-blue-500" />
									<p className="text-xs sm:text-sm font-medium text-blue-700">
										Dateien hier ablegen
									</p>
								</>
							) : (
								<>
									<Upload className="h-6 sm:h-8 w-6 sm:w-8 mx-auto mb-1 sm:mb-2 text-gray-400" />
									<p className="text-xs sm:text-sm font-medium text-gray-700">
										Ziehen Sie Dateien hierher oder klicken
										Sie zum Hochladen
									</p>
									<p className="text-xs text-gray-500 mt-1">
										PDF-Dateien (Lieferscheine,
										Kostenvoranschläge, Rechnungen, etc.)
									</p>
								</>
							)}
						</div>
						<Input
							ref={fileInputRef}
							id="fileUpload"
							type="file"
							multiple
							accept=".pdf,.png,.jpg,.jpeg"
							onChange={handleFileChange}
							className="hidden"
						/>
					</div>
					{uploadedFiles.length > 0 && (
						<div className="space-y-1 sm:space-y-2 mt-2 sm:mt-3">
							<p className="text-xs sm:text-sm font-medium text-gray-700">
								Hochgeladene Dateien ({uploadedFiles.length})
							</p>
							{uploadedFiles.map((file, index) => (
								<div
									key={index}
									className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg border border-gray-200"
								>
									<span className="text-xs sm:text-sm text-gray-700 truncate mr-2">
										{file.name}
									</span>
									<button
										type="button"
										onClick={() => removeFile(index)}
										className="text-gray-500 hover:text-red-500 transition-colors flex-shrink-0"
										aria-label="Datei entfernen"
									>
										<X className="h-4 w-4" />
									</button>
								</div>
							))}
						</div>
					)}
				</div>

				{/* Migration Decision */}
				<div className="space-y-2 sm:space-y-4">
					<div>
						<h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1 sm:mb-2">
							Migration von Dental lab XP
						</h3>
						<p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-4">
							Falls Sie von Dental lab XP migrieren möchten,
							aktivieren Sie diese Option.
						</p>
					</div>
					<div className="flex items-center space-x-2 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
						<Checkbox
							id="migrationDecision"
							checked={migrationDecision}
							onCheckedChange={(checked) =>
								setMigrationDecision(checked as boolean)
							}
						/>
						<label
							htmlFor="migrationDecision"
							className="text-xs sm:text-sm font-medium leading-none cursor-pointer"
						>
							Ich möchte von Dental lab XP migrieren
						</label>
					</div>
				</div>
			</div>
		</OnboardingStep>
	);
};
